import React from 'react';
import styled, { keyframes } from 'styled-components';
import { getColorScheme } from '../theme/colorSchemes';

const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', country: 'gb' },
  { code: 'fr', name: 'French', nativeName: 'Français', country: 'fr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', country: 'es' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', country: 'br' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', country: 'cn' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', country: 'in' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', country: 'sa' },
  { code: 'fa', name: 'Farsi', nativeName: 'فارسی', country: 'ir' },
  { code: 'tl', name: 'Filipino', nativeName: 'Tagalog', country: 'ph' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', country: 'kr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', country: 'ru' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', country: 'il' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', country: 'tr' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', country: 'ua' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', country: 'vn' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', country: 'jp' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', country: 'pk' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', country: 'it' },
];

const FLAG_URL = (country) => `https://flagcdn.com/w80/${country}.png`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.05); }
`;

const Container = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 28px 16px 20px; height: 100%; overflow-y: auto;
`;

const GlobeWrapper = styled.div`
  width: 52px; height: 52px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 12px; animation: ${fadeIn} 0.5s ease-out;
  background: ${p => {
    const c = getColorScheme(p.$scheme);
    return `linear-gradient(135deg, ${c.primary}, ${c.primaryLight})`;
  }};
  box-shadow: ${p => `0 6px 20px ${getColorScheme(p.$scheme).accentShadow}`};
`;

const GlobeSVG = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const Title = styled.h2`
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 18px; font-weight: 700; margin: 0 0 4px 0;
  color: #1a2332; text-align: center;
  animation: ${fadeIn} 0.5s ease-out 0.1s both;
`;

const Subtitle = styled.p`
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-size: 13px; color: #6b7a8d; margin: 0 0 22px 0;
  text-align: center; animation: ${fadeIn} 0.5s ease-out 0.2s both;
`;

const Grid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 10px; width: 100%; max-width: 310px;
`;

const FlagButton = styled.button`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px; border-radius: 14px;
  border: 2px solid ${p => getColorScheme(p.$scheme).accentBorder};
  background: #ffffff; cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  animation: ${fadeIn} 0.4s ease-out ${p => 0.15 + p.$index * 0.05}s both;
  position: relative; overflow: hidden;
  &::before {
    content: ''; position: absolute; inset: 0; border-radius: 12px;
    opacity: 0; transition: opacity 0.25s ease;
    background: ${p => `linear-gradient(135deg, ${getColorScheme(p.$scheme).primary}08, ${getColorScheme(p.$scheme).primaryLight}05)`};
  }
  &:hover {
    border-color: ${p => getColorScheme(p.$scheme).primary};
    transform: translateY(-3px);
    box-shadow: ${p => `0 8px 24px ${getColorScheme(p.$scheme).accentShadow}, 0 0 0 1px ${getColorScheme(p.$scheme).primary}20`};
    &::before { opacity: 1; }
  }
  &:active { transform: translateY(-1px); }
`;

const FlagImg = styled.img`
  width: 36px; height: 24px; border-radius: 3px; object-fit: cover;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15); flex-shrink: 0;
  transition: transform 0.25s ease;
  ${FlagButton}:hover & { animation: ${pulse} 0.6s ease-in-out; }
`;

const LangInfo = styled.div`
  display: flex; flex-direction: column; align-items: flex-start; min-width: 0;
`;

const LangName = styled.span`
  font-size: 13px; font-weight: 600; color: #1a2332; line-height: 1.3;
`;

const NativeName = styled.span`
  font-size: 11px; color: #94a3b8; line-height: 1.3;
`;

const LanguageSelector = ({ onSelect, darkMode = false, scheme = 'blue' }) => {
  return (
    <Container>
      <GlobeWrapper $scheme={scheme}><GlobeSVG /></GlobeWrapper>
      <Title>Choose your language</Title>
      <Subtitle>Select a language to continue</Subtitle>
      <Grid>
        {LANGUAGES.map((lang, i) => (
          <FlagButton key={lang.code} $scheme={scheme} $index={i} onClick={() => onSelect(lang.code)}>
            <FlagImg src={FLAG_URL(lang.country)} alt={`${lang.name} flag`} loading="eager" />
            <LangInfo>
              <LangName>{lang.name}</LangName>
              <NativeName>{lang.nativeName}</NativeName>
            </LangInfo>
          </FlagButton>
        ))}
      </Grid>
    </Container>
  );
};

export { LANGUAGES };
export default LanguageSelector;
